import { toImmutable } from 'nuclear-js';

import _DataPopulation from './data-population';
import _Reactor from 'data/reactor';
import _Getters from 'data/getters';
import _Actions from 'data/actions';
import _ComparisonUtils from 'components/student-profile/modules/comparisons/utils';
import _DataUtils from 'data/utils';
import _PayloadBuilders from 'data/payloadBuilders';
import _AuthUtils from 'utils/auth';
import _GrowthUtils from 'components/student-profile/modules/growth-goals/utils';
import _SessionStorageUtils from './session-storage/utils';
import _TermUtils from 'utils/terms';

import Analytics from 'utils/analytics';

import CommonValues from 'resources/common-values';
import { courses } from '@nwea/subject-course-data';

// parameterize this to make unit testing easier
export function loader(
    dataPopulation = _DataPopulation,
    reactor = _Reactor,
    getters = _Getters,
    actions = _Actions,
    dataUtils = _DataUtils,
    payloadBuilders = _PayloadBuilders,
    growthUtils = _GrowthUtils,
    termUtils = _TermUtils,
    sessionStorageUtils = _SessionStorageUtils,
    comparisonUtils = _ComparisonUtils,
    authUtils = _AuthUtils
) {
    return {
        loadPartnerData(partnerBid, userBid) {
            if (!partnerBid) {
                throw new Error('unexpected condition: partnerBid not set');
            }
            if (!userBid) {
                throw new Error('unexpected condition: userBid not set');
            }
            const partnerTermsPromise = dataPopulation.populateTermsForPartner(partnerBid);
            const userLinksPromise = dataPopulation.getLinksForUser(partnerBid);
            const getLinkingStudiesPromise = dataPopulation.getLinkingStudiesForPartner(partnerBid);
            const promiseArray = [partnerTermsPromise, userLinksPromise, getLinkingStudiesPromise];
            return promiseArray;
        },
        loadRitQuintiles(studentTermData, subjects) {
            if (!subjects || subjects.isEmpty()) throw new Error('unexpected condition: could not find subjects');
            const ritQuintiles = reactor.evaluate(getters.partner.ritQuintiles);
            if (!ritQuintiles) throw new Error('unexpected condition: could not get ritQuintiles');

            const payload = payloadBuilders.buildRitQuintilePayload(studentTermData, subjects, ritQuintiles);
            const ritQuintilePromises = [];
            if (payload && payload.length > 0) {
                // call once per subject, because it was too large to call all at once...the payload for
                //   a sample student was still around 19K
                toImmutable(payload)
                    .groupBy((p) => p.get('subject'))
                    .forEach((v, k) => {
                        ritQuintilePromises.push(dataPopulation.getRitQuintiles(v));
                    });
                return ritQuintilePromises;
            } else {
                //console.log('no ritQuintile data needed to be loaded');
                return [Promise.resolve()];
            }
        },
        loadDistrictAverages(partnerBid, studentTermData) {
            const subjects = reactor.evaluate(getters.runtime.subjects);
            if (!subjects || subjects.isEmpty()) throw new Error('unexpected condition: could not find subjects');
            const districtAverages = reactor.evaluate(getters.partner.districtAverages);
            if (!districtAverages) throw new Error('unexpected condition: could not get districtAverages');

            const payload = payloadBuilders.buildDistrictAveragePayload(studentTermData, subjects, districtAverages);

            if (payload && payload.length > 0) {
                return dataPopulation.getDistrictAverages(partnerBid, payload);
            } else {
                // console.log('no districtAverage data needed to be loaded');
                return Promise.resolve();
            }
        },
        loadGrowthProjections(studentBid, studentTermData, testEvents) {
            const mostRecentTestEventsBySubject = dataUtils.findMostRecentTestEventsBySubject(testEvents);

            const payload = mostRecentTestEventsBySubject
                ? payloadBuilders.buildGrowthProjectionsPayload(mostRecentTestEventsBySubject, studentTermData)
                : null;

            if (payload && payload.length > 0) {
                return dataPopulation.getGrowthProjections(studentBid, payload);
            } else {
                // indicate that this part of the data load is complete
                return Promise.resolve(() => {
                    actions.student.createEmptyGrowthProjectionsForStudent(studentBid);
                });
            }
        },
        loadGrowthPercentiles(studentBid, studentTermData, testEvents) {
            const payload = payloadBuilders.buildGrowthNormPercentilesPayload(studentTermData, testEvents);
            if (payload && payload.length > 0) {
                return dataPopulation.getGrowthPercentiles(studentBid, payload);
            } else {
                // indicate that this part of the data load is complete
                return Promise.resolve(() => {
                    actions.student.createEmptyGrowthPercentilesForStudent(studentBid);
                });
            }
        },
        loadLinkingStudyData(studentTermData, studentBid, testEvents) {
            const studies = reactor.evaluate(getters.partner.linkingStudies);

            //ForEach subject, build a payload and get the linking studies
            const linkingStudyPromises = [];
            courses.forEach((s) => {
                const measurementScaleID = parseInt(s.measurementScaleID);
                const firstOpenTerm = growthUtils.getFirstOpenTerm(studentTermData, measurementScaleID, testEvents);
                if (firstOpenTerm && studies) {
                    const linkingStudiesPayload = payloadBuilders.buildRequestPayloadForLinkingStudies(
                        measurementScaleID,
                        firstOpenTerm.get('termNumber'),
                        studentTermData,
                        studies
                    );

                    linkingStudyPromises.push(
                        dataPopulation.getLinkingStudies(studentBid, measurementScaleID, linkingStudiesPayload)
                    );
                } else {
                    //Create Empty Linking Study if first open term does not exist
                    linkingStudyPromises.push(
                        Promise.resolve(() => {
                            actions.student.createEmptyLinkingStudyData(studentBid, measurementScaleID);
                        })
                    );
                }
            });
            return linkingStudyPromises;
        },
        computeAndStoreStudentTermData(studentBid, studentInfo) {
            const partnerTerms = reactor.evaluate(getters.partner.terms);
            if (!partnerTerms) throw new Error('unexpected condition: could not find partnerTerms');

            const termsWithInfo = dataUtils.createTermDataForStudent(partnerTerms, studentInfo);
            actions.student.loadStudentTermData(studentBid, termsWithInfo);

            return termsWithInfo;
        },
        loadLearningContinuumData(testEvents) {
            // load for all test keys
            const testkeys = dataUtils.findUniqueTestKeys(testEvents);
            const lcPromises = [];
            if (testkeys) {
                testkeys.forEach((testkey) => {
                    const loaded = reactor.evaluate(getters.learningContinuum.learningContinuumLoadedForTest(testkey));

                    if (!loaded) {
                        lcPromises.push(dataPopulation.getLearningContinuumData(testkey));
                    } else {
                        if (!__BATCH__) {
                            console.log(`Learning Continuumm Data loaded for ${testkey}`);
                        }
                        return lcPromises.push(Promise.resolve());
                    }
                });
            }

            return lcPromises;
        },
        loadNormDataForGoalSetting(studentBid, subjects, testEvents, studentTermData) {
            const partnerTerms = reactor.evaluate(getters.partner.terms);
            if (!partnerTerms) {
                throw new Error('unexpected condition: could not find partnerTerms');
            }

            const currentTerm = termUtils.findCurrentTerm(partnerTerms);
            const normDataPromises = [];
            if (currentTerm) {
                subjects.forEach((s) => {
                    const testEventsForSubject = testEvents.filter(
                        (testEvent) => testEvent.get('measurementScaleID') === s.get('bid')
                    );

                    const maxTestEventForSubject = testEventsForSubject.maxBy((te) => te.get('termNumber'));
                    /* TODO:CSN This could be a place where growth goals has potential bugs
                     * The subjectBid might need to be MSID */
                    const firstOpenTerm = growthUtils.getFirstOpenTerm(partnerTerms, s.get('bid'), testEvents);
                    const goalSettingTerms = growthUtils.findGoalSettingTerms(
                        partnerTerms,
                        firstOpenTerm,
                        currentTerm,
                        maxTestEventForSubject,
                        studentTermData
                    );

                    if (!goalSettingTerms.isEmpty()) {
                        const growthGoal = growthUtils.canSetGrowthGoal(testEventsForSubject, firstOpenTerm);
                        const payloadFunction = growthGoal
                            ? (term) => {
                                  return payloadBuilders.buildNormDataForGrowthGoalPayload(
                                      s.get('bid'),
                                      term.get('termNumber'),
                                      maxTestEventForSubject,
                                      studentTermData
                                  );
                              }
                            : (term) => {
                                  return payloadBuilders.buildNormDataForPerformanceGoalPayload(
                                      s.get('bid'),
                                      term.get('termNumber'),
                                      studentTermData
                                  );
                              };
                        const dataPopulationFunction = growthGoal
                            ? (term, payload) => {
                                  return dataPopulation.getNormDataForSettingGrowthGoals(
                                      studentBid,
                                      s.get('bid'),
                                      term.get('termNumber'),
                                      payload
                                  );
                              }
                            : (term, payload) => {
                                  return dataPopulation.getNormDataForSettingPerformanceGoals(
                                      studentBid,
                                      s.get('bid'),
                                      term.get('termNumber'),
                                      payload
                                  );
                              };

                        goalSettingTerms.forEach((t) => {
                            const payload = payloadFunction(t);
                            if (payload) {
                                normDataPromises.push(dataPopulationFunction(t, payload));
                            } else {
                                normDataPromises.push(
                                    Promise.resolve(() => {
                                        actions.student.createEmptyNormDataForGoalSetting(
                                            studentBid,
                                            t.get('termNumber'),
                                            s.get('bid')
                                        );
                                    })
                                );
                            }
                        });
                    } else {
                        normDataPromises.push(
                            Promise.resolve(() => {
                                actions.student.createEmptyNormDataForGoalSetting(
                                    studentBid,
                                    CommonValues.mostRecentTermNumber,
                                    s.get('bid')
                                );
                            })
                        );
                    }
                });
            } else {
                // If missing firstOpenTerm, add in empty data for the most recent term so that the studentLoaded
                //     getter will know that the load for goal setting norm data is complete.
                subjects.forEach((s) => {
                    normDataPromises.push(
                        Promise.resolve(() => {
                            actions.student.createEmptyNormDataForGoalSetting(
                                studentBid,
                                CommonValues.mostRecentTermNumber,
                                s.get('bid')
                            );
                        })
                    );
                });
            }
            return normDataPromises;
        },
        loadHighlights(partnerBid, studentBid) {
            return dataPopulation.getHighlights(partnerBid, studentBid);
        },
        loadStudentData(
            studentBid,
            allCompleteCallback = () => {},
            animationTransitionDelay = null,
            partOfAsyncLoad = false
        ) {
            if (!studentBid) {
                throw new Error('unexpected condition: studentBid must be set');
            } else if (studentBid === CommonValues.EMPTY_STUDENT_PLACEHOLDER.key) {
                actions.student.createEmptyStudentPlaceholder();
                actions.runtime.allowAdvanceStudent(true);
                actions.student.setStudentLoaded(studentBid);
                allCompleteCallback();
            } else if (reactor.evaluate(getters.student.studentExistsInStore(studentBid))) {
                if (animationTransitionDelay) {
                    setTimeout(() => {
                        actions.runtime.allowAdvanceStudent(true);
                    }, animationTransitionDelay);
                } else {
                    actions.runtime.allowAdvanceStudent(true);
                }

                allCompleteCallback();
            } else {
                /*
                    The intent of this next section is to check local cache for the indicated student.
                    If it exists, then load the information from cache, otherwise we start the various API calls for data.
                    If we find the student in cache, then we need to ensure that the data is complete. If any part
                    of the data is missing, then the various API calls are made instead of using the cache.
                 */
                const studentData = sessionStorageUtils.loadStudentData(studentBid);
                let dataIsComplete = false;
                if (studentData) {
                    let studentList = {};
                    studentList[studentBid] = studentData;
                    dataIsComplete = dataUtils.studentIsLoaded(toImmutable(studentList), studentBid, true, false);
                    if (!dataIsComplete) {
                        Analytics.logErrorToDatadog(new Error('Loading from cache failed for student.'));
                        console.log('>>> ' + studentBid + ' >>> Cached data is incomplete. Reloading from server.');
                    }
                } else {
                    console.warn('studentData for student is empty', studentBid);
                }
                if (dataIsComplete) {
                    // this is a special case when navigating through the arrows--delay the load so that
                    //    the highlights transition works
                    if (animationTransitionDelay) {
                        setTimeout(() => {
                            actions.student.storeEntireDataMap(studentBid, studentData);
                            actions.runtime.allowAdvanceStudent(true);
                            actions.student.setStudentLoaded(studentBid);
                        }, animationTransitionDelay);
                    } else {
                        actions.student.storeEntireDataMap(studentBid, studentData);
                        actions.runtime.allowAdvanceStudent(true);
                        actions.student.setStudentLoaded(studentBid);
                    }
                    allCompleteCallback();
                } else {
                    const partnerBid = reactor.evaluate(getters.partner.partnerBid);
                    if (!partnerBid) throw new Error('unexpected condition: partnerBid not set, but user data loaded');

                    // add the student bid to the store
                    actions.student.addStudentBid(studentBid);
                    const combinedInfoPromise = dataPopulation.getCombinedStudentInfo(partnerBid, studentBid);

                    combinedInfoPromise
                        .then((combinedInfo) => {
                            if (!combinedInfo) {
                                /* NOTE: Checking for !combinedInfo to avoid console error during logout process from 401.
                                See TP: 216202 */
                                return null;
                            }

                            const subjectsToUseForRequests = dataUtils.combineSubjectsAndMeasurementScaleIDs(
                                combinedInfo.measurementScaleIDsForStudent
                            );

                            const testEvents = toImmutable(combinedInfo.testEvents);
                            const studentTermData = this.computeAndStoreStudentTermData(
                                studentBid,
                                toImmutable(combinedInfo.studentInfo)
                            );
                            allCompleteCallback(); // kick off next student load before the rest of this stuff
                            const batchedPromises = [
                                this.loadHighlights(partnerBid, studentBid),
                                this.loadRitQuintiles(studentTermData, subjectsToUseForRequests),
                                this.loadDistrictAverages(partnerBid, studentTermData),
                                this.loadGrowthProjections(studentBid, studentTermData, testEvents),
                                this.loadNormDataForGoalSetting(
                                    studentBid,
                                    subjectsToUseForRequests,
                                    testEvents,
                                    studentTermData
                                ),
                                this.loadLinkingStudyData(studentTermData, studentBid, testEvents),
                                this.loadGrowthPercentiles(studentBid, studentTermData, testEvents),
                            ];
                            const additionalActions = [
                                () => {
                                    actions.student.completeStudentLoad(studentBid);
                                },
                                () => {
                                    actions.runtime.allowAdvanceStudent(true);
                                },
                            ];

                            this.batchUpdateStore(batchedPromises, additionalActions)
                                .then(() => {
                                    actions.student.setStudentLoaded(studentBid);
                                })
                                .catch((error) => {
                                    console.error('LOAD ERROR', error);
                                });
                        })
                        .catch((reason) => {
                            //TODO: figure out what to do on the page when student data cannot be loaded
                            console.warn(reason);
                        });
                } // if (dataIsComplete)
            }
        },
        /**
         * This function expects an array of promise arrays.  The supplied promises should resolve to a function.
         * When executed the function should update the nuclear store.
         *
         * @param batchPromises An array of promise arrays, whose resolved promises should supply a
         * function which when executed will update the store with a data payload.
         *
         * @param additionalActions An optional array of functions which when executed will update the store.
         *
         * @returns {Promise} Which will execute all promises and their returned store update functions.
         */
        batchUpdateStore(batchPromises, additionalActions = []) {
            const executeStoreUpdate = (storeUpdateFn) => {
                if (typeof storeUpdateFn === 'function') {
                    storeUpdateFn();
                }
            };

            return new Promise((resolve, reject) => {
                // Update the nuclear store via a batch update to reduce churn for active observers.
                // Observers will be notified once the entire batch operation has completed.

                if (batchPromises && batchPromises.length > 0) {
                    // Promises may exist as a multi dimensional array - flatten first
                    const promises = batchPromises.reduce((acc, item) => {
                        return acc.concat(item);
                    }, []);
                    Promise.all(promises)
                        .then((pendingStoreActions) => {
                            reactor.batch(() => {
                                pendingStoreActions.forEach((pendingStoreAction) => {
                                    executeStoreUpdate(pendingStoreAction);
                                });

                                additionalActions.forEach((pendingStoreAction) => {
                                    executeStoreUpdate(pendingStoreAction);
                                });
                            });
                            resolve();
                        })
                        .catch((error) => {
                            console.error(error);
                            reject(error);
                        });
                } else {
                    resolve(() => {});
                }
            });
        },
        loadStudentDataForBatchPrint(partnerBid, studentBid) {
            // add the student bid to the store
            Analytics.logMessageToDatadog(
                'In loadStudentDataForBatchPrint for partner Bid : ',
                partnerBid + ', Student Bid :' + studentBid
            );
            actions.student.addStudentBid(studentBid);

            return new Promise((resolve, reject) => {
                const combinedInfoPromise = dataPopulation.getCombinedStudentInfo(partnerBid, studentBid);
                combinedInfoPromise
                    .then((combinedInfo) => {
                        const testEvents = toImmutable(combinedInfo.testEvents);
                        Analytics.logMessageToDatadog(
                            'In loadStudentDataForBatchPrint combinedInfo Data : ',
                            JSON.stringify(combinedInfo)
                        );
                        const studentTermData = this.computeAndStoreStudentTermData(
                            studentBid,
                            toImmutable(combinedInfo.studentInfo)
                        );
                        const subjectsToUseForRequests = dataUtils.combineSubjectsAndMeasurementScaleIDs(
                            combinedInfo.measurementScaleIDsForStudent
                        );
                        Analytics.logMessageToDatadog(
                            'In loadStudentDataForBatchPrint subjectsToUseForRequests Data : ',
                            JSON.stringify(subjectsToUseForRequests)
                        );
                        const batchedPromises = [
                            this.loadHighlights(partnerBid, studentBid),
                            this.loadRitQuintiles(studentTermData, subjectsToUseForRequests),
                            this.loadDistrictAverages(partnerBid, studentTermData),
                            this.loadGrowthProjections(studentBid, studentTermData, testEvents),
                            this.loadNormDataForGoalSetting(
                                studentBid,
                                subjectsToUseForRequests,
                                testEvents,
                                studentTermData
                            ),
                            this.loadLinkingStudyData(studentTermData, studentBid, testEvents),
                            this.loadLearningContinuumData(testEvents),
                            this.loadGrowthPercentiles(studentBid, studentTermData, testEvents),
                        ];
                        this.batchUpdateStore(batchedPromises)
                            .then(() => {
                                resolve();
                            })
                            .catch((error) => {
                                console.error('Error retrieving secondary data: ' + error);
                                Analytics.logMessageToDatadog('Error in loadStudentDataForBatchPrint : ', error);
                                reject(error);
                            });
                    })
                    .catch((error) => {
                        console.error('Error during combined info retrieval for student ' + studentBid + ': ' + error);
                        reject(error);
                    });
            });
        },
        loadSchoolsForTerm(termBid) {
            const partnerBid = reactor.evaluate(getters.partner.partnerBid);
            if (!partnerBid) throw 'unexpected condition: partnerBid not set';
            const educatorBid = reactor.evaluate(getters.user.educatorBid);
            if (!educatorBid) throw 'unexpected condition: educatorBid not set';
            if (!termBid) throw `termBid must be set`;
            dataPopulation.getSchoolsForInstructorAndTerm(partnerBid, termBid, educatorBid);
        },

        loadInstructorsForSchool(termBid, schoolBid) {
            const partnerBid = reactor.evaluate(getters.partner.partnerBid);
            if (!partnerBid) throw 'unexpected condition: partnerBid not set';
            if (!termBid || !schoolBid) throw `termBid and schoolBid must be set`;

            dataPopulation.getInstructorsForSchoolAndTerm(partnerBid, schoolBid, termBid);
        },

        loadClassesForInstructor(termBid, schoolBid, instructorBid) {
            const partnerBid = reactor.evaluate(getters.partner.partnerBid);
            if (!partnerBid) throw 'unexpected condition: partnerBid not set';
            if (!termBid || !schoolBid || !instructorBid) throw `termBid, schoolBid, and instructorBid must be set`;

            dataPopulation.getClassesForInstructor(partnerBid, schoolBid, instructorBid, termBid);
        },

        loadStudentsForClass(termBid, schoolBid, classBid, cb = () => {}) {
            const partnerBid = reactor.evaluate(getters.partner.partnerBid);
            if (!partnerBid) throw 'unexpected condition: partnerBid not set';
            if (!termBid || !schoolBid || !classBid) throw `termBid, schoolBid, and classBid must be set`;

            dataPopulation.getStudentsForClass(partnerBid, schoolBid, classBid, termBid, cb);
        },

        loadStudents(studentBid, customCallback) {
            actions.runtime.initialLoadDone(false);
            const nextStudent = reactor.evaluate(getters.student.nextStudent(studentBid));
            const nextBid = nextStudent ? nextStudent.get('studentBid', null) : null;
            const prevStudent = reactor.evaluate(getters.student.previousStudent(studentBid));
            const previousBid = prevStudent ? prevStudent.get('studentBid', null) : null;

            // note: stagger the next and previous callbacks too
            const previousStudentCallback = () => {
                if (previousBid) {
                    this.loadStudentData(
                        previousBid,
                        () => {
                            actions.runtime.initialLoadDone(true);
                        },
                        null,
                        true
                    );
                } else {
                    actions.runtime.initialLoadDone(true);
                }
            };
            const nextStudentCallback = () => {
                if (nextBid) {
                    this.loadStudentData(nextBid, previousStudentCallback, null, true);
                } else {
                    previousStudentCallback();
                }
            };

            let callback = nextStudentCallback;

            if (customCallback && typeof customCallback === 'function') {
                // After initial student load call our custom callback
                // before initiating the next/prvious student loads
                callback = () => {
                    customCallback();
                    nextStudentCallback();
                };
            }

            this.loadStudentData(studentBid, callback, null, true);
        },

        reloadStudent(studentBid) {
            sessionStorageUtils.clearStudentData();
            actions.student.clearStudentData();
            this.loadStudents(studentBid);
        },

        clearUserData() {
            actions.runtime.clearState();
            sessionStorageUtils.clearStudentData();
        },

        logOutUserIfNotAuthorized(profileData) {
            if (!authUtils.isUserAuthorized(profileData.userProfile.roles)) {
                this.clearUserData();
                authUtils.redirectUserToLogin();
            }
        },

        loadStudentSelectionData() {
            actions.user.setProfileCheckCompleted(false);
            const currentUser = reactor.evaluate(getters.user.userInfo);
            
            const callback = (profileData) => {
               // this.logOutUserIfNotAuthorized(profileData);
                const partnerBid = profileData.userProfile.partnerBid,
                    userBid = profileData.userProfile.userBid;
                // if there is no current user, store information and load partner data
                if (!currentUser.get('userBid')) {
                    this.loadPartnerData(partnerBid, userBid);
                } else if (currentUser.get('userBid') !== profileData.userProfile.userBid) {
                    this.clearUserData();
                    this.loadPartnerData(partnerBid, userBid);
                }
                actions.user.storeUserData(profileData); // marks profile check completed
            };

            const failureCallback = () => {
                this.clearUserData();
                // not sure if redirecting to auth is right, but it seems safest
                window.location = '/auth/login';
            };

            return dataPopulation.getUserProfile(callback, failureCallback);
        },

        /*
         * Initializes setProfileCheckCompleted to false and evaluates currentUser from store.
         * Compares the store's currentUser with the auth'd user from an auth request to
         * /auth/rest/profile (via dataPopulation.getUserProfile) and if they don't match -
         * clear the store's userInfo and start the loadPartnerData process.
         * */
        loadStudentProfileData(studentBid) {
            actions.user.setProfileCheckCompleted(false);
            const currentUser = reactor.evaluate(getters.user.userInfo);
            const callback = (profileData) => {
              //  this.logOutUserIfNotAuthorized(profileData);

                let redirectToStudentSelection = false;

                const partnerBid = profileData.userProfile.partnerBid;
                const userBid = profileData.userProfile.userBid;
                // if there is no current user, store information, load partner and student data
                if (!currentUser.get('userBid')) {
                    const partnerDataPromises = this.loadPartnerData(partnerBid, userBid);
                    Promise.all(partnerDataPromises)
                        .then((values) => this.loadStudents(studentBid))
                        .catch(() => {});
                } else if (currentUser.get('userBid') !== profileData.userProfile.userBid) {
                    // if the user in the store is different than what the profile service says,
                    //        clear out data in local storage, load the partner data, and send
                    //        'em to the student selection screen
                    this.clearUserData();
                    this.loadPartnerData(partnerBid, userBid);
                    redirectToStudentSelection = true;
                } else {
                    this.loadStudents(studentBid);
                }

                actions.user.storeUserData(profileData); // marks profile check completed
                return redirectToStudentSelection;
            };
            const failureCallback = () => {
                this.clearUserData();
                window.location = '/auth/login';
                return false; // do not redirect to student selected
            };

            return dataPopulation.getUserProfile(callback, failureCallback);
        },
    };
}

export default loader();
